import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Panel, ErrorBox } from "@/components/Widgets";
import { API, df, errMsg } from "@/lib/api";
import StrategyView from "@/components/StrategyView";

export default function Strategy() {
  const [strategy, setStrategy] = useState(null);
  const [history, setHistory] = useState([]);
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [chars, setChars] = useState(0);
  const [error, setError] = useState(null);

  const loadHistory = () => axios.get(`${API}/strategy/history`).then((r) => setHistory(r.data));
  useEffect(() => {
    axios.get(`${API}/strategy/latest`).then((r) => setStrategy(r.data));
    loadHistory();
  }, []);

  const generate = async () => {
    setBusy(true); setChars(0); setError(null);
    try {
      const { data: { job_id } } = await axios.post(`${API}/strategy/generate`, { focus });
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await axios.get(`${API}/strategy/job/${job_id}`);
        setChars(job.chars);
        if (job.status === "error") throw new Error(job.error);
        if (job.status === "done") {
          const r = await axios.get(`${API}/strategy/${job.strategy_id}`);
          setStrategy(r.data); toast.success("Strategia generata"); loadHistory();
          return;
        }
      }
      throw new Error("Timeout: la generazione ha impiegato troppo tempo");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const openOld = (id) => axios.get(`${API}/strategy/${id}`).then((r) => setStrategy(r.data));

  return (
    <>
      <PageHeader eyebrow="Claude Sonnet 4.5 · analisi dei tuoi dati" title="Strategia AI" desc="L'AI incrocia performance dei contenuti, demografica, commenti e ricerche hashtag per dirti cosa pubblicare, quando, con quali keyword e come portare i follower alla vendita di EasyHost." />
      <section className="border-2 border-[#2A4B3C] bg-[#FFFFFF] p-6 mb-6 rise" data-testid="strategy-generator">
        <div className="flex flex-col md:flex-row gap-4">
          <Textarea data-testid="focus-input" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus opzionale, es. 'lancio offerta estate per B&B in Puglia' o 'aumentare le richieste demo'" className="rounded-none flex-1 min-h-[70px]" />
          <Button data-testid="generate-strategy-btn" onClick={generate} disabled={busy} className="rounded-full bg-[#C05C3D] hover:bg-[#A94E32] h-12 px-8 self-start md:self-center">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}{busy ? "Analisi in corso..." : strategy ? "Rigenera strategia" : "Genera strategia"}
          </Button>
        </div>
        {busy && (
          <div className="mt-4" data-testid="generation-progress">
            <div className="h-1 bg-[#F1F1EC] overflow-hidden"><div className="h-full bg-[#2A4B3C]" style={{ width: `${Math.min(97, chars / 150)}%`, transition: "width .3s" }} /></div>
            <p className="text-xs text-[#5C5C58] mt-2 font-mono-num">Claude sta scrivendo la strategia... {chars} caratteri (circa 2-3 minuti)</p>
          </div>
        )}
        {history.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 items-center">
            <span className="label mr-1">Storico</span>
            {history.slice(0, 8).map((h) => <button key={h.id} data-testid={`history-${h.id}`} onClick={() => openOld(h.id)} className={`text-xs border px-2.5 py-1 transition-colors ${strategy?.id === h.id ? "border-[#2A4B3C] text-[#2A4B3C]" : "border-[#E8E8E4] hover:border-[#2A4B3C]"}`}>{df(h.created_at)}{h.focus ? ` · ${h.focus.slice(0, 24)}` : ""}</button>)}
          </div>
        )}
      </section>
      {error && <ErrorBox msg={error} />}
      {strategy ? <StrategyView s={strategy.data} /> : !busy && <Panel><p className="text-sm text-[#5C5C58]">Nessuna strategia ancora. Premi "Genera strategia" per ottenere il tuo piano editoriale personalizzato.</p></Panel>}
    </>
  );
}
